// @ts-check
/**
 * Mounted once, high in the tree, so the AI panel can open over any route.
 *
 * Whatever opens the panel passes the content to show - usually an element rendering AIPanel with its own
 * data. The provider owns only the overlay: the portal, the backdrop, focus and dismissal. It knows nothing
 * about what is being shown or where it was opened from.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { AIPanelContext } from './aiPanelContext';
import useFocusTrap from './useFocusTrap';

export const AIPanelProvider = ({ children }) => {
  const [content, setContent] = useState(/** @type {import('react').ReactNode} */ (null));
  const containerRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const returnFocusRef = useRef(/** @type {HTMLElement|null} */ (null));
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const openedAtPathRef = useRef(pathname);

  const isOpen = content !== null;

  const openPanel = useCallback((/** @type {import('react').ReactNode} */ nextContent) => {
    // Captured before anything moves focus, so it is the trigger that gets it back.
    if (!isOpen) {
      returnFocusRef.current = /** @type {HTMLElement|null} */ (document.activeElement);
    }

    openedAtPathRef.current = pathnameRef.current;
    setContent(nextContent);
  }, [isOpen]);

  const closePanel = useCallback(() => {
    setContent(null);
  }, []);

  useFocusTrap(containerRef, isOpen, closePanel);

  // Focus into the panel on open, and back to the trigger on close if it is still on the page.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const container = containerRef.current;
    const dialog = /** @type {HTMLElement|null} */ (container?.querySelector('[role="dialog"]'));
    (dialog ?? container)?.focus();

    const returnFocusTo = returnFocusRef.current;

    return () => {
      if (returnFocusTo?.isConnected) {
        returnFocusTo.focus();
      }
    };
  }, [isOpen]);

  // The page behind does not scroll while the panel is open.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Following a link out of the panel navigates away, so the panel closes with the page it covered.
  useEffect(() => {
    if (isOpen && pathname !== openedAtPathRef.current) {
      closePanel();
    }
  }, [pathname, isOpen, closePanel]);

  const value = useMemo(() => ({ openPanel, closePanel, isOpen }), [openPanel, closePanel, isOpen]);

  return (
    <AIPanelContext.Provider value={value}>
      {children}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="ai-panel-overlay">
          <div
            className="absolute inset-0 bg-black/30"
            aria-hidden="true"
            onClick={closePanel}
            data-testid="ai-panel-backdrop"
          />
          <div
            ref={containerRef}
            tabIndex={-1}
            className="relative h-full w-full outline-none sm:max-w-xl"
            data-testid="ai-panel-container"
          >
            {content}
          </div>
        </div>,
        document.body
      )}
    </AIPanelContext.Provider>
  );
};

AIPanelProvider.propTypes = {
  children: PropTypes.node,
};

export default AIPanelProvider;
